import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import Image from 'global/Image';
import If from 'global/Common/If';
import { formatNumber } from 'utils/number';
import tradingDataStore from 'lib/stores/tradingData';
import SplitLotRow from 'global/Securities/components/Table/SplitLotRow';
import { PreviewContainer, PreviewItem } from 'global/TradingModal/styled';
import { isRedirectToNewCore } from 'features/securities/utils';
import { FC, useMemo } from 'react';
import { CorporateActionStockType } from 'constants/trading';
import { getSellAmountInvestedFee } from 'global/Securities/utils/formula';
import {
  expiryOptions,
  orderTypeOptions,
} from 'global/Securities/constants/transactionForm';
import { SPLIT_ORDER_COPY } from 'global/Securities/constants/transaction';
import {
  SellOrderType,
  SplitOrderMethodEnum,
} from 'global/Securities/types/transaction';
import { iPreviewData } from 'typings/transactionForm';
import { generateSellData } from '../../utils/generateFeeData';
import { expiryOption, lotSize } from '../../constant';
import realTradingStore from '../store';

interface SellPreviewProps {
  showExchangeFee: () => void;
  data: iPreviewData;
}

const SellPreview: FC<SellPreviewProps> = ({ showExchangeFee, data }) => {
  const {
    company,
    portfolioDetail,
    configFormula: { data: configFormula },
  } = tradingDataStore();

  const {
    price,
    shares,
    symbol,
    isSplitOrderActive,
    splitQuantity,
    splitRangeMin,
    splitRangeMax,
    type,
    gtc,
  } = data;

  const expiry = expiryOption[gtc];
  const isMarket = type === SellOrderType.MarketOrder;
  const isLimit = type === SellOrderType.LimitOrder;
  const isDayTrade = data.isDayTrade && isLimit;

  const { splitMethod } = realTradingStore();
  const {
    data: { type_company: productType },
  } = company;
  const {
    data: { price_average_fee },
  } = portfolioDetail;

  const lot = shares / lotSize;

  const isCorporateAction = useMemo(
    () => CorporateActionStockType.includes(productType.toLowerCase()),
    [productType],
  );

  const generatedSellData = generateSellData(
    configFormula,
    lot,
    price,
    price_average_fee,
    isDayTrade,
  );

  const { STOCKBIT_FEE: brokerFee, TOTAL_EXCHANGE_FEE: exchangeFee } =
    generatedSellData;

  return (
    <PreviewContainer direction="column" width="100%">
      <PreviewItem>
        <Text height="19px">Stock</Text>
        <Text height="19px" family="bold">
          {symbol}
        </Text>
      </PreviewItem>

      <PreviewItem>
        <Text height="19px">Order Type</Text>
        <Text height="19px">{orderTypeOptions.Sell[type]}</Text>
      </PreviewItem>

      <PreviewItem>
        <Text height="19px">Expiry</Text>
        <Text height="19px">
          {isMarket ? 'Fill and Kill' : expiryOptions[expiry]}
        </Text>
      </PreviewItem>

      <SplitLotRow
        lot={lot}
        disabled={isSplitOrderActive || !isRedirectToNewCore()}
      />

      <If condition={!isMarket}>
        <PreviewItem>
          <Text height="19px">Price</Text>
          <Text height="19px">{formatNumber(price, false, '0,00', true)}</Text>
        </PreviewItem>
      </If>

      {isSplitOrderActive && (
        <>
          <PreviewItem>
            <Text height="19px">Split Quantity</Text>
            <Text height="19px">{formatNumber(splitQuantity, false)}</Text>
          </PreviewItem>
          <PreviewItem>
            <Text height="19px">Split Method</Text>
            <Text height="19px">{SPLIT_ORDER_COPY[splitMethod]}</Text>
          </PreviewItem>
          <If condition={splitMethod === SplitOrderMethodEnum.RANDOM}>
            <PreviewItem>
              <Text height="19px">Split Range Lot</Text>
              <Text height="19px">
                {`${formatNumber(splitRangeMin, false)} - ${formatNumber(
                  splitRangeMax,
                  false,
                )}`}
              </Text>
            </PreviewItem>
          </If>
        </>
      )}

      <PreviewItem>
        <Text height="19px">{isMarket ? 'Estimated ' : ''}Proceed Amount</Text>
        <Text height="19px">
          Rp {formatNumber(price * lot * lotSize, false, '0,00', true)}
        </Text>
      </PreviewItem>

      <PreviewItem>
        <Text height="19px">{isMarket ? 'Estimated ' : ''}Broker Fee</Text>
        <Text height="19px">
          {brokerFee === 0
            ? 'Free'
            : `Rp ${formatNumber(brokerFee, false, '0,00', true)}`}
        </Text>
      </PreviewItem>

      <PreviewItem>
        <Flex align="center">
          <Flex align="center">
            <Text height="19px">
              {isMarket ? 'Estimated ' : ''}Exchange Fee
            </Text>
            <Image
              src="/icon/information.svg"
              alt="fee-info"
              onClick={showExchangeFee}
              style={{ marginLeft: '5px', width: '12px', cursor: 'pointer' }}
            />
          </Flex>
        </Flex>
        <Text height="19px">
          Rp {formatNumber(exchangeFee, false, '0,00', true)}
        </Text>
      </PreviewItem>

      <PreviewItem>
        <div>
          <Text height="19px" family="bold">
            {isMarket ? 'Estimated ' : ''}Proceeds (Net Fee)
          </Text>
        </div>
        <Text height="19px" family="bold">
          Rp{' '}
          {formatNumber(
            getSellAmountInvestedFee(lot, price, isCorporateAction, isDayTrade),
            false,
          )}
        </Text>
      </PreviewItem>
    </PreviewContainer>
  );
};

export default SellPreview;
